import { safe } from "@orpc/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import type { CreateProject } from "@workspace/contracts/projects";
import { toast } from "sonner";
import { orpc } from "@/api/orpc";
import { ProjectPdfDownloadButton } from "@/features/project/components/project-pdf-download-button";
import { ProjectForm } from "@/features/project/form";
import { getError } from "@/utils/error";

export const Route = createFileRoute("/_authenticated/projects/$projectId/update")({
  component: RouteComponent,
  loader: async ({ params, context: { orpc, queryClient } }) => {
    const [error, project] = await safe(
      queryClient.fetchQuery(orpc.admin.projects.findById.queryOptions({ input: { id: params.projectId } })),
    );

    if (error) throw notFound();

    const [featureOptions, cityList, distanceOptions, districtList] = await Promise.all([
      queryClient.fetchQuery(orpc.features.optionsAdmin.queryOptions()),
      queryClient.fetchQuery(orpc.cities.options.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.distances.options.queryOptions({ input: {} })),
      queryClient.fetchQuery(orpc.districts.optionsAdmin.queryOptions()),
    ]);

    return { project, featureOptions, cityList, distanceOptions, districtList };
  },
});

function RouteComponent() {
  const { featureOptions, cityList, distanceOptions, districtList, project } = Route.useLoaderData();
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();

  const { mutateAsync } = useMutation(orpc.admin.projects.update.mutationOptions());

  const onSubmit = async (formValue: CreateProject) => {
    try {
      await mutateAsync({ id: project.id, ...formValue });
      toast.success("Project has been saved successfully");
      queryClient.invalidateQueries({
        queryKey: orpc.admin.projects.list.queryKey({ input: {} }),
      });
      queryClient.invalidateQueries({
        queryKey: orpc.admin.projects.findById.queryKey({ input: { id: project.id } }),
      });
      navigate({ to: "/projects" });
    } catch (error) {
      const msg = getError(error, "Failed to update project");
      toast.error(msg);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 rounded-xl border bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-semibold text-xl">Edit project</h1>
          <p className="truncate text-muted-foreground text-sm">
            {project.code}
            {project.projectName ? ` - ${project.projectName}` : ""}
          </p>
        </div>

        <ProjectPdfDownloadButton project={project} />
      </div>

      <ProjectForm
        project={{
          ...project,
          title: project.translations.find((t) => t.lang === "ar")?.title ?? "",
          overview: project.translations.find((t) => t.lang === "ar")?.overview ?? "",
          metaTitle: project.translations.find((t) => t.lang === "ar")?.metaTitle ?? undefined,
          metaDescription: project.translations.find((t) => t.lang === "ar")?.metaDescription ?? undefined,
          images: {
            items: project.images,
            thumbnail: project.thumbnail ?? undefined,
          },
          video: project.video ?? undefined,
          featureIds: project.projectFeatures.map((f) => f.featureId),
          distances: project.projectDistances.map((d) => ({ distanceId: d.distanceId, distance: Number(d.distance) })),
          lang: "ar",
        }}
        onSubmit={onSubmit}
        featureOptions={featureOptions}
        cityList={cityList}
        districtList={districtList}
        distanceOptions={distanceOptions}
      />
    </div>
  );
}
